<template>
  <div
    class="checkbox-input"
    :class="[
      { [$class('loading')]: loading },
    ]"
  >
    <input
      :id="id"
      v-model="localModelValue"
      :aria-invalid="ariaInvalid"
      :aria-describedby="ariaDescribedby"
      v-bind="name === 'consent' ? { 'aria-required': 'true' } : {}"
      class="checkbox-input__input visually-hidden"
      :class="{ [$class('disabled')]: disabled }"
      :disabled="disabled || loading"
      :name="name"
      type="checkbox"
      :value="value"
      @change="updateVModel(localModelValue)"
    >

    <label
      class="checkbox-input__label text-body-m-regular"
      :for="id"
    >
      <div
        v-if="colorSwatch"
        class="checkbox-input__label-swatch"
        :style="`background: ${colorSwatch}`"
      />

      <span
        class="checkbox-input__label-text"
        v-html="label"
      />
    </label>
  </div>
</template>

<script>

/**
 * Vue: Checkbox input (checkbox-input)
 * -----------------------------------------------------------------------------
 * Checkbox input utility component.
 *
 * @param {Boolean|null} [ariaInvalid] - `aria-invalid` attribute.
 * @param {Boolean} [disabled] - Input is disabled.
 * @param {String} id - ID for input.
 * @param {String} label - Input label.
 * @param {Boolean} [loading] - Loading state.
 * @param {Boolean} modelValue - Current value provided by v-model.
 * @param {String} [name] - Input name attribute.
 * @param {String} [value] - Optional value if a hardcoded value is required.
 * @param {String} [colorSwatch] - Renders in the label if set.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */
export default {
  name: 'CheckboxInput',

  props: {
    ariaInvalid: {
      type: [Boolean, null],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
      required: true,
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Boolean,
      default: false,
      required: true,
    },
    name: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    colorSwatch: {
      type: String,
      default: '',
    },
    ariaDescribedby: {
      type: String,
      default: '',
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: false,
    }
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Emit update v-model event.
     * @param {Boolean} value - Updated value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value)
    },
  },
}
</script>

<style lang="scss">
@import './checkbox-input';
</style>
